import React, { createContext, useState, useContext, useEffect } from 'react';
import { authAPI } from '../utils/api';

const AuthContext = createContext(null);

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Check if user is already logged in on mount
  useEffect(() => {
    const initAuth = async () => {
      // Check if this is an SSO callback (URL has sessionId param)
      // If so, skip initAuth - Login.js will handle authentication
      const urlParams = new URLSearchParams(window.location.search);
      const ssoSessionId = urlParams.get('sessionId');
      if (ssoSessionId) {
        console.log('[AuthContext] SSO callback detected in URL, skipping initAuth - Login.js will handle');
        setLoading(false);
        return;
      }

      const sessionId = localStorage.getItem('sessionId');
      console.log('[AuthContext] initAuth - sessionId in localStorage:', sessionId ? sessionId.substring(0, 8) + '...' : 'none');

      if (sessionId) {
        try {
          // SECURITY: Always verify session with server - NEVER trust localStorage
          const response = await authAPI.getCurrentUser();
          console.log('[AuthContext] initAuth - /api/auth/me success, user:', response.data.user?.email);
          setUser(response.data.user);
        } catch (error) {
          console.error('[AuthContext] initAuth - /api/auth/me failed:', error.response?.status, error.response?.data?.code);
          // If session is invalid, clear it - BUT only if sessionId hasn't changed
          // (protects against race condition with SSO login storing a new sessionId)
          const currentSessionId = localStorage.getItem('sessionId');
          if (error.response?.status === 401 && currentSessionId === sessionId) {
            console.log('[AuthContext] initAuth - clearing invalid session');
            localStorage.removeItem('sessionId');
            setUser(null);
          } else if (currentSessionId !== sessionId) {
            console.log('[AuthContext] initAuth - sessionId changed during verification, skipping clear (new login occurred)');
          }
          // For network errors, keep trying in background
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  // Request magic link
  const requestMagicLink = async (email) => {
    try {
      setError(null);
      const response = await authAPI.requestMagicLink(email);
      return response.data;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Failed to send magic link';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  // Verify magic link and login
  const verifyMagicLink = async (token) => {
    try {
      setError(null);
      const response = await authAPI.verifyMagicLink(token);
      const { sessionId, user: userData } = response.data;

      // SECURITY: Only store session ID in localStorage, NOT user data or role
      localStorage.setItem('sessionId', sessionId);

      // User data comes from server verification, not localStorage
      setUser(userData);
      return userData;
    } catch (err) {
      const errorMsg = err.response?.data?.error || 'Invalid or expired magic link';
      setError(errorMsg);
      throw new Error(errorMsg);
    }
  };

  // Login with session ID (for direct login)
  const login = async (userData, sessionId) => {
    console.log('[AuthContext] login() called - email:', userData?.email, 'role:', userData?.role, 'sessionId:', sessionId?.substring(0, 8) + '...');
    // SECURITY: Only store session ID, NOT user data
    localStorage.setItem('sessionId', sessionId);
    setUser(userData);
    console.log('[AuthContext] login() complete - localStorage set, user state updated');
  };

  // Logout
  const logout = async () => {
    try {
      // Revoke session on server
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    }

    // Clear local storage
    localStorage.removeItem('sessionId');
    setUser(null);
  };

  // Check if user is authenticated
  const isAuthenticated = () => {
    const hasUser = !!user;
    const hasSession = !!localStorage.getItem('sessionId');
    const result = hasUser && hasSession;
    // Log only when result is false (to help debug auth failures)
    if (!result) {
      console.log('[AuthContext] isAuthenticated() = false - user:', hasUser ? user?.email : 'null', 'sessionId:', hasSession ? 'present' : 'MISSING');
    }
    return result;
  };

  // SECURITY WARNING: These role checks are for UI display only!
  // NEVER use these for authorization - server always verifies
  // They are safe because user data comes from server, not localStorage

  // Check if user has specific role (UI ONLY - not for security)
  const hasRole = (roles) => {
    if (!user) return false;
    if (Array.isArray(roles)) {
      return roles.includes(user.role);
    }
    return user.role === roles;
  };

  // Check if user is admin (UI ONLY - not for security)
  const isAdmin = () => {
    return user?.role === 'admin';
  };

  // Check if user is interviewer (UI ONLY - not for security)
  const isInterviewer = () => {
    return user?.role === 'interviewer' || user?.role === 'admin';
  };

  // Check if user is candidate (UI ONLY - not for security)
  const isCandidate = () => {
    return user?.role === 'candidate';
  };

  const value = {
    user,
    loading,
    error,
    requestMagicLink,
    verifyMagicLink,
    login,
    logout,
    isAuthenticated,
    hasRole,
    isAdmin,
    isInterviewer,
    isCandidate,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

// Custom hook to use auth context
export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export default AuthContext;
